import React from 'react';
import {
    Box,
    Button,
    ButtonGroup,
    Badge,
} from '@mui/material';
import { DesignCreationType } from 'src/services/DesignConductorService';
import { getDesignTypeIcon, getDesignTypeColor, getDesignTypeCustomColor } from 'src/utils/designTypeUtils';

interface DesignTypeToggleProps {
    selectedType: DesignCreationType;
    onSelectType: (type: DesignCreationType) => void;
    results: {
        [K in DesignCreationType]: {
            isLoading: boolean;
            error: Error | null;
            data: any;
        };
    };
}

const getStatusColor = (result: { error: Error | null; data: any }): 'error' | 'warning' | undefined => {
    if (!result.error && result.data) {
        return undefined; // Success - no badge
    }
    if (result.error?.message?.includes('404')) {
        return 'warning';
    }
    if (result.error) {
        return 'error';
    }
    return undefined;
};

export const DesignTypeToggle: React.FC<DesignTypeToggleProps> = ({
    selectedType,
    onSelectType,
    results,
}) => {
    const designTypes: DesignCreationType[] = ['Static', 'Dynamic', 'AutoLayout'];

    return (
        <Box sx={{ mb: 2 }}>
            <ButtonGroup
                fullWidth
                aria-label="design type selector"
                sx={{
                    gap: 2,
                }}
            >
                {designTypes.map((type) => {
                    const result = results[type];
                    const statusColor = getStatusColor(result);
                    const isSelected = selectedType === type;
                    const buttonColor = getDesignTypeColor(type);
                    const customColor = getDesignTypeCustomColor(type);

                    return (
                        <Badge
                            color={statusColor}
                            invisible={!statusColor}
                            key={type}
                            sx={{
                                flex: 1,
                                '& .MuiBadge-badge': {
                                    top: 8,
                                    right: 8,
                                    left: 'auto',
                                },
                            }}
                            variant="dot"
                        >
                            <Button
                                fullWidth
                                color={buttonColor}
                                startIcon={getDesignTypeIcon(type)}
                                sx={{
                                    color: ((): string | undefined => {
                                        if (customColor && !isSelected) return customColor;
                                        return undefined;
                                    })(),
                                    borderRadius: 0,
                                    '&:first-of-type': {
                                        borderTopLeftRadius: 4,
                                        borderBottomLeftRadius: 4,
                                    },
                                    '&:last-of-type': {
                                        borderTopRightRadius: 4,
                                        borderBottomRightRadius: 4,
                                    },
                                    ...(customColor && isSelected && {
                                        backgroundColor: customColor,
                                        color: 'common.white',
                                        '&:hover': {
                                            backgroundColor: customColor,
                                            opacity: 0.9,
                                        },
                                    }),
                                }}
                                variant={isSelected ? 'contained' : 'outlined'}
                                onClick={(): void => onSelectType(type)}
                            >
                                {type}
                            </Button>
                        </Badge>
                    );
                })}
            </ButtonGroup>
        </Box>
    );
};
